// Turns userOrders.additionalDetails.invoiceRxCheck (written by the API after an eVital
// ORDER_SAVE) into what the order-page panel shows. Pure: no React, no fetching.

const NOT_BILLED = 'NOT_BILLED';

// Below this score the "closest invoice item" is unrelated noise (e.g. Oxcarbazepine vs TELMA),
// so the table only shows it for near misses such as a substitute brand.
const CLOSEST_HINT_MIN_SCORE = 0.5;

const hasBill = (billNumber) => billNumber !== undefined && billNumber !== null && String(billNumber).trim() !== '';

// One row per prescribed medicine (its matched invoice item on the same row), then one row per
// invoice item no prescribed medicine matched. Every name is shown exactly as OCR read it.
const buildRows = (lines, extraInvoiceItems) => [
    ...lines.map((line) => {
        const billed = line.status !== NOT_BILLED;
        return {
            prescription: line.rxName,
            invoice: billed ? line.matchedInvoiceName || null : null,
            closest: !billed && line.matchedInvoiceName && (line.score || 0) >= CLOSEST_HINT_MIN_SCORE
                ? line.matchedInvoiceName
                : null,
            status: billed ? 'MATCHED' : 'NOT_ON_INVOICE',
        };
    }),
    ...extraInvoiceItems.map((name) => ({ prescription: null, invoice: name, closest: null, status: 'ONLY_ON_INVOICE' })),
];

// One invoice item can satisfy several prescribed lines, so the invoice is counted by distinct name.
const buildCounts = (lines, extraInvoiceItems) => {
    const billed = lines.filter((line) => line.status !== NOT_BILLED);
    const matchedInvoiceNames = new Set(billed.map((line) => line.matchedInvoiceName).filter(Boolean));
    return {
        prescription: lines.length,
        invoice: matchedInvoiceNames.size + extraInvoiceItems.length,
        matched: billed.length,
    };
};

/**
 * @param {object|null|undefined} check - additionalDetails.invoiceRxCheck
 * @param {string|number|null} [currentBillNumber] - additionalDetails.billNumber
 * @returns {{
 *   state: 'WAITING_FOR_BILL'|'NOT_CHECKED'|'MATCHED'|'MISMATCH'|'NO_RX'|'ERROR',
 *   tone: 'neutral'|'success'|'warning'|'danger',
 *   badge: string,
 *   message: string|null,
 *   isStale: boolean,
 *   missing: object[],
 *   billed: object[],
 *   extraInvoiceItems: string[],
 *   checkedAt: string|null,
 *   rows: Array<{ prescription: string|null, invoice: string|null, closest: string|null,
 *                 status: 'MATCHED'|'NOT_ON_INVOICE'|'ONLY_ON_INVOICE' }>,
 *   counts: { prescription: number, invoice: number, matched: number },
 *   defaultExpanded: boolean,
 * }}
 */
export const getInvoiceRxCheckView = (check, currentBillNumber = null) => {
    const base = {
        isStale: false, missing: [], billed: [], extraInvoiceItems: [], checkedAt: null, message: null,
        rows: [], counts: { prescription: 0, invoice: 0, matched: 0 }, defaultExpanded: false,
    };

    if (!check) {
        return hasBill(currentBillNumber)
            ? { ...base, state: 'NOT_CHECKED', tone: 'neutral', badge: 'Not checked yet', message: 'The invoice has not been compared with the prescription yet.' }
            : { ...base, state: 'WAITING_FOR_BILL', tone: 'neutral', badge: 'No bill yet', message: 'The check runs once the eVital bill is created.' };
    }

    const lines = Array.isArray(check.lines) ? check.lines : [];
    const extraInvoiceItems = Array.isArray(check.extraInvoiceItems) ? check.extraInvoiceItems : [];
    const common = {
        ...base,
        checkedAt: check.checkedAt || null,
        missing: lines.filter((line) => line.status === NOT_BILLED),
        billed: lines.filter((line) => line.status !== NOT_BILLED),
        extraInvoiceItems,
        rows: buildRows(lines, extraInvoiceItems),
        counts: buildCounts(lines, extraInvoiceItems),
        // Open the comparison table when something is missing; keep it tidy otherwise.
        defaultExpanded: check.status === 'MISMATCH',
        // The bill changed after this check ran, so the verdict describes an older bill.
        isStale: hasBill(currentBillNumber) && hasBill(check.billNumber) && String(check.billNumber) !== String(currentBillNumber),
    };

    switch (check.status) {
        case 'MATCHED':
            return { ...common, state: 'MATCHED', tone: 'success', badge: 'All prescribed medicines billed' };
        case 'MISMATCH': {
            const count = common.missing.length || check.missingCount || 0;
            return { ...common, state: 'MISMATCH', tone: 'danger', badge: `${count} prescribed medicine${count !== 1 ? 's' : ''} not on invoice` };
        }
        case 'NO_RX':
            return { ...common, state: 'NO_RX', tone: 'neutral', badge: 'No prescription to compare', message: 'This order has no prescription document, so the invoice was not checked.' };
        case 'ERROR':
            return { ...common, state: 'ERROR', tone: 'warning', badge: 'Check could not complete', message: check.error || 'The check failed.' };
        default:
            return { ...common, state: 'NOT_CHECKED', tone: 'neutral', badge: 'Not checked yet', message: null };
    }
};
